/**
 * Core-100 R4 W2 — CORE-S: a creator authors and submits; a creator admin publishes or rejects (with a reason).
 * Three separate acts, each decided by the server (proof core_s_w2_authoring_audit_view_as):
 *   author  — creator_content_submissions INSERT/UPDATE while status = 'draft' (RLS: the creator's own, private)
 *   submit  — submit_creator_submission (idempotent)
 *   publish — decide_creator_submission (creator admin only; never their own work; creates the catalogue artifact and
 *             the submitting creator's authorship link in one transaction; a decided submission is final)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type SubmissionStatus = 'draft' | 'submitted' | 'published' | 'rejected';

export interface CreatorSubmission {
  id: string;
  creator_id: string;
  author_user_id: string;
  title: string;
  description: string | null;
  artifact_type: string;
  access_url: string;
  access_type: string;
  estimated_time_minutes: number | null;
  status: SubmissionStatus;
  submitted_at: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  published_artifact_id: string | null;
  created_at: string;
}

export interface NewDraft {
  creatorId: string;
  title: string;
  accessUrl: string;
  artifactType: string;
  description?: string;
}

const TABLE = 'creator_content_submissions' as never;

export function useMySubmissions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['creator-submissions', 'mine', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from(TABLE).select('*').eq('author_user_id' as never, user!.id as never)
        .order('created_at' as never, { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as CreatorSubmission[];
    },
    enabled: !!user?.id,
  });
}

/** The creator admin's review queue: submitted work only (drafts are private to their author). */
export function useSubmissionQueue(enabled: boolean) {
  return useQuery({
    queryKey: ['creator-submissions', 'queue'],
    queryFn: async () => {
      const { data, error } = await supabase.from(TABLE).select('*').eq('status' as never, 'submitted' as never)
        .order('submitted_at' as never, { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as CreatorSubmission[];
    },
    enabled,
  });
}

export function useCreateDraft() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (d: NewDraft) => {
      const { error } = await supabase.from(TABLE).insert({
        creator_id: d.creatorId, author_user_id: user!.id, title: d.title.trim(), access_url: d.accessUrl.trim(),
        artifact_type: d.artifactType, description: d.description?.trim() || null,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-submissions'] }),
  });
}

export function useSubmitSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('submit_creator_submission' as never, { p_submission_id: id } as never);
      if (error) throw error;
      return data as unknown as 'SUBMITTED' | 'ALREADY_SUBMITTED';
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['creator-submissions'] }),
  });
}

export function useDecideSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; decision: 'publish' | 'reject'; reason?: string }) => {
      const { data, error } = await supabase.rpc('decide_creator_submission' as never,
        { p_submission_id: args.id, p_decision: args.decision, p_reason: args.reason ?? null } as never);
      if (error) throw error;
      return data as unknown as { status: SubmissionStatus; artifact_id?: string; already: boolean };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['creator-submissions'] });
      qc.invalidateQueries({ queryKey: ['admin-all-artifacts-with-metrics'] });
    },
  });
}
